import { useContext, useMemo } from "react";
import { httpsCallable } from "firebase/functions";
import { MyUserDataContext } from "../../contexts";
import { useFunctions } from "../../helpers/firebaseContext";
import { CtcAction, CtcPoStatusCode, CtcRole, CTC_PO_STATUS_TAGS, ctcAvailableActions } from "../../../../shared/ctcLifecycle";
import { ctcActorRestriction, ctcLaneCustomerRestriction } from "../../../../shared/ctcAccess";
import type { CtcReportFilters } from "../../../../shared/ctcReports";
import { CtcBeneficiary, CtcBulkSettleRequest, CtcBulkSettleResponse, CtcCustomer, CtcPoStatusTag, CtcPurchaseOrder, CustomClaims, CtcTransitionRequest } from "../../types";

/** The test project lets one person make and check an order (same as the server). */
export const ctcTestEnvironment = import.meta.env.VITE_FIREBASE_PROJECT_ID === "kfh-capital-crm-test";

/**
 * What this user may do on an order right now: the lifecycle actions of their roles, without maker / checker
 * roles for customers outside their Microsoft groups, minus what ctcActorRestriction forbids (own order,
 * another dealer's order…). Same rules as the server; the order page's buttons and the lists' » arrow use it.
 */
export const ctcActionsFor = (
    po: Pick<CtcPurchaseOrder, "customerId" | "tags" | "lastAction" | "makerEmail" | "transferRequestedByEmail" | "assignedDealerEmail">,
    user: CtcUser,
    customer: Partial<CtcCustomer> | undefined,
): CtcAction[] => {
    const outsideGroup = !!ctcLaneCustomerRestriction(user, po.customerId);
    const roles: CtcRole[] = user.roles.filter((role) => !(outsideGroup && (role === "maker" || role === "checker")));
    const set = new Set<CtcAction>();
    for (const role of roles) ctcAvailableActions(po.tags.poStatus.code, role, (customer ?? {}) as CtcCustomer, po.lastAction?.action).forEach((a) => set.add(a));
    return [...set].filter((action) => !ctcActorRestriction(user, po, action, { allowSelfCheck: ctcTestEnvironment }));
};

/** Actions that move an order to its next step (not reassign / cancel / delete / reject / recording a WhatsApp answer). */
export const CTC_NEXT_STEP_ACTIONS: ReadonlySet<CtcAction> = new Set([
    CtcAction.submit, CtcAction.approve, CtcAction.accept, CtcAction.confirm_purchase, CtcAction.request_transfer,
    CtcAction.approve_transfer, CtcAction.confirm_transfer, CtcAction.request_sell, CtcAction.confirm_sell, CtcAction.complete_collection,
]);

/** Use current wording even on older orders; TagBadge displays shortName, not title. */
export const ctcStatusTag = (stored?: CtcPoStatusTag) => {
    if (!stored) return undefined;
    const current = CTC_PO_STATUS_TAGS[stored.code] ?? stored;
    return { ...current, shortName: current.title };
};

/** Roles the signed-in user holds, derived from the ctc_* claims (mirrors functions-ctc/common.ts). */
export const ctcRolesOf = (claims: CustomClaims | undefined): CtcRole[] => {
    if (!claims) return [];
    const roles: CtcRole[] = [];
    if (claims.ctc_maker) roles.push("maker");
    if (claims.ctc_checker) roles.push("checker");
    if (claims.ctc_supervisor) roles.push("supervisor");
    if (claims.ctc_dealer) roles.push("dealer");
    if (claims.ctc_backoffice) roles.push("backoffice");
    if (claims.ctc_admin || claims.admin) roles.push("admin");
    if (claims.ctc_viewer) roles.push("viewer");
    return roles;
};

export interface CtcUser {
    email: string;
    roles: CtcRole[];
    customerId?: string; // KFH users are bound to the KFH customer
    isDesk: boolean; // brokerage / KFH Capital
    isAdmin: boolean;
    /** Desk user with the ctc_backoffice or ctc_admin claim: may edit ctc_config (firestore.rules isCtcBackoffice). */
    isCtcBackoffice: boolean;
    /** Only the ctc_viewer role: sees everything allowed, takes no action. */
    isViewOnly: boolean;
    /** Customers whose Microsoft group the user is in: maker/checker may act only for these (admins: any). */
    groupCustomerIds: string[];
    has: (role: CtcRole) => boolean;
}

export const useCtcUser = (): CtcUser => {
    const userContext = useContext(MyUserDataContext);
    const claims = (userContext.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined) ?? undefined;
    const email = userContext.user?.email ?? userContext.msal?.account?.username ?? "";
    return useMemo(() => {
        const roles = ctcRolesOf(claims);
        const isDesk = claims?.ctc_staff === true && ["kfhbrokerage.com", "kfhcapital.com.kw"].includes(email.toLowerCase().split("@")[1]);
        return {
            email: email.toLowerCase(),
            roles,
            customerId: claims?.ctc_customer_id,
            isDesk,
            isAdmin: roles.includes("admin"),
            isCtcBackoffice: isDesk && (claims?.ctc_backoffice === true || claims?.ctc_admin === true),
            isViewOnly: roles.length > 0 && roles.every((r) => r === "viewer"),
            groupCustomerIds: claims?.ctc_group_customer_ids ?? [],
            has: (role: CtcRole) => roles.includes(role),
        };
    }, [claims, email]);
};

export interface CtcTransitionResponse {
    poId: string;
    status: CtcPoStatusCode;
    effects: string[];
    pendingEffects: string[];
}

export interface CtcSaveOrderRequest {
    poId?: string;
    customerId?: string;
    clientRefNo?: string;
    beneficiary: CtcBeneficiary;
    amount: number;
    currencyId: string;
    commissionId: string;
    valueDate?: string;
    dynamicFields?: Record<string, string | number | boolean>;
    uploads?: string[];
}

export const useCtcCallables = () => {
    const functions = useFunctions();
    return useMemo(() => ({
        transition: httpsCallable<CtcTransitionRequest, CtcTransitionResponse>(functions, "ctcTransition"),
        saveOrder: httpsCallable<CtcSaveOrderRequest, { poId: string; referenceNumber: string }>(functions, "ctcSaveOrder"),
        retryEffects: httpsCallable<{ poId: string }, { ran: string[]; failed: string[] }>(functions, "ctcRetryEffects"),
        resolveDelivery: httpsCallable<{ poId: string; deliveryId: string; outcome: "sent" | "not_sent"; sid?: string; reason: string }, { resolved: boolean }>(functions, "ctcResolveDelivery"),
        markSettled: httpsCallable<{ poId: string; side: "customer" | "broker"; settled: boolean; reference?: string; settledAt?: string }, { settled: boolean }>(functions, "ctcMarkSettled"),
        bulkMarkSettled: httpsCallable<CtcBulkSettleRequest, CtcBulkSettleResponse>(functions, "ctcBulkMarkSettled", { timeout: 540000 }),
        deleteCustomer: httpsCallable<{ customerId: string }, { deleted: true }>(functions, "ctcDeleteCustomer"),
        deleteCommissionProfile: httpsCallable<{ commissionId: string }, { deleted: true; unlinkedCustomers: number }>(functions, "ctcDeleteCommissionProfile"),
        saveAttachmentTemplate: httpsCallable<{ id?: string; attachmentNumber: 1 | 2 | 3 | 4 | 5; name: string; text_en: string; text_ar: string; makeDefault?: boolean; note?: string }, { id: string; version: number }>(functions, "ctcSaveAttachmentTemplate"),
        requestReport: httpsCallable<{ report: "summary" | "details"; from: string; to: string; columns: string[]; filters?: CtcReportFilters }, { requestId: string; recipients: number }>(functions, "ctcRequestReport"),
        resendReport: httpsCallable<{ requestId: string }, { recipients: number }>(functions, "ctcResendReport"),
        addAttachments: httpsCallable<{ poId: string; paths: string[] }, { added: number }>(functions, "ctcAddAttachments"),
        updateAttachmentTemplate: httpsCallable<{ id: string; action: "make_default" | "retire" | "unretire" | "delete" }, { ok: true; movedCustomers: number }>(functions, "ctcUpdateAttachmentTemplate"),
    }), [functions]);
};

/** Human labels for actions on the detail page action bar. */
export const CTC_ACTION_LABELS: Record<CtcAction, { label: string; color: string; needsReason?: boolean }> = {
    [CtcAction.submit]: { label: "Submit", color: "blue" },
    [CtcAction.approve]: { label: "Approve", color: "green" },
    [CtcAction.reject]: { label: "Reject", color: "red", needsReason: true },
    [CtcAction.accept]: { label: "Accept & assign dealer", color: "green" },
    [CtcAction.reject_by_ctc]: { label: "Reject", color: "red", needsReason: true },
    [CtcAction.confirm_purchase]: { label: "Confirm purchase", color: "green" },
    [CtcAction.beneficiary_approve_transfer]: { label: "Record transfer approval", color: "green", needsReason: true },
    [CtcAction.beneficiary_reject_transfer]: { label: "Record transfer rejection", color: "red", needsReason: true },
    [CtcAction.request_transfer]: { label: "Request ownership transfer", color: "blue" },
    [CtcAction.approve_transfer]: { label: "Approve transfer", color: "green" },
    [CtcAction.reject_transfer]: { label: "Reject transfer", color: "red", needsReason: true },
    [CtcAction.confirm_transfer]: { label: "Confirm transfer", color: "green" },
    [CtcAction.reject_transfer_by_ctc]: { label: "Reject transfer", color: "red", needsReason: true },
    [CtcAction.beneficiary_sell]: { label: "Record sale authorisation", color: "green", needsReason: true },
    [CtcAction.beneficiary_collect]: { label: "Record collection request", color: "blue", needsReason: true },
    [CtcAction.beneficiary_reject_sell]: { label: "Record refusal to sell", color: "red", needsReason: true },
    [CtcAction.request_sell]: { label: "Request sale for beneficiary", color: "blue" },
    [CtcAction.reject_sell_by_customer]: { label: "Reject sale", color: "red", needsReason: true },
    [CtcAction.confirm_sell]: { label: "Confirm sale", color: "green" },
    [CtcAction.reject_sell_by_ctc]: { label: "Reject sale", color: "red", needsReason: true },
    [CtcAction.complete_collection]: { label: "Confirm collection", color: "green" },
    [CtcAction.cancel_after_completion]: { label: "Cancel after completion", color: "orange", needsReason: true },
    [CtcAction.cancel]: { label: "Cancel order", color: "red", needsReason: true },
    [CtcAction.delete]: { label: "Delete", color: "gray", needsReason: true },
    [CtcAction.reassign]: { label: "Reassign dealer", color: "gray" },
    [CtcAction.beneficiary_change_answer]: { label: "Reopen beneficiary question", color: "yellow", needsReason: true },
};

/** Value date as dd/mm/yyyy: stored as "YYYY-MM-DD" (older orders: a Timestamp, read as the Kuwait day). */
export const ctcValueDay = (v: unknown): string | undefined => {
    if (typeof v === "string") return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
    return (v as { toDate?: () => Date } | undefined)?.toDate?.().toLocaleDateString("en-CA", { timeZone: "Asia/Kuwait" });
};
export const formatValueDate = (v: unknown) => ctcValueDay(v)?.split("-").reverse().join("/");

export const formatMoney = (amount: number | undefined, currency?: string) =>
    amount === undefined ? "" : `${amount.toLocaleString(undefined, { maximumFractionDigits: 3 })}${currency ? ` ${currency}` : ""}`;
